import { merge, present } from '@art-suite/art-core-ts';
import { css } from '@emotion/react';
import { useMemo, useState } from 'react';

import { isScanEditable } from '@kaiizen/kai-pro-api';
import { useAllOrgSettings, useAuth, useCreateContractorScanShareLink, useFeatureFlags, useScanIsEditableCheck } from '@kaiizen/kai-pro-api-react-state';

import { DeltaIcon, LargeContractorIcon } from '@/Client/Icons';
import { DefaultContractorPicker } from '@/Client/Partials';
import { useScanEditingContextSlice, useToast } from '@/Client/State';
import { useConfirmationModal } from '@/Client/State/ConfirmationModalContext';
import { COLORS2, LAYOUT2, TYPOGRAPHY2 } from '@/Client/Styles';
import { Button, PropsCard } from '@/Client/Widgets';
import { ContractorIdsByTrade } from '@kaiizen/kai-scan-template-system';
import { getOverviewTradeOrder, TradeSettingsTrade } from './contractorTradeList';

const CONTAINER_STYLE = css`
  label: CONTAINER_STYLE;
  display: flex;
  flex-direction: row;
  gap: ${LAYOUT2.spacing2}px;
  padding: ${LAYOUT2.spacing2}px 0;
  background: var(--background);
  align-items: center;
  justify-content: center;
`;

const MAIN_COLUMN_STYLE = css`
  label: MAIN_COLUMN_STYLE;
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: ${LAYOUT2.spacingHalf}px;
`;

const MAIN_TITLE_STYLE = css`
  label: MAIN_TITLE_STYLE;
  ${TYPOGRAPHY2.xLargeBold}
  color: var(--text);
  display: flex;
  align-items: center;
  gap: ${LAYOUT2.spacingHalf}px;
`;

const SHORT_INSTRUCTION_STYLE = css`
  label: SHORT_INSTRUCTION_STYLE;
  ${TYPOGRAPHY2.xSmall}
  color: var(--text2);
`;

const normalizeContractorId = (contractorId: string | null | undefined) => {
  if (present(contractorId)) return contractorId;
  return null;
};

const contractorHasChanged = (
  trade: string,
  defaultContractorsByTrade: ContractorIdsByTrade | undefined,
  baselineDefaultContractorsByTrade: ContractorIdsByTrade | undefined,
) => {
  return normalizeContractorId(defaultContractorsByTrade?.[trade]) !== normalizeContractorId(baselineDefaultContractorsByTrade?.[trade]);
};

const CopyShareLinkButton = ({ scanId, orgContractorId }: { scanId: string; orgContractorId: string }) => {
  const { createContractorScanShareLink, isCreatingShareLink } = useCreateContractorScanShareLink();
  const { showToast, showErrorToast } = useToast();

  const onCopy = async () => {
    try {
      const { url } = await createContractorScanShareLink({ scanId, orgContractorId });
      await navigator.clipboard.writeText(url);
      showToast('Contractor share link copied to clipboard', 'success');
    } catch (error) {
      showErrorToast(error, 'create a contractor share link');
    }
  };

  return (
    <Button buttonStyle='white' onClick={onCopy} disabled={isCreatingShareLink}>
      {isCreatingShareLink ? 'Copying…' : 'Copy share link'}
    </Button>
  );
};

export const ContractorsPanel = () => {
  const [editMode, setEditMode] = useState(false);
  const { allOrgSettings } = useAllOrgSettings();
  const { featureFlags } = useFeatureFlags();
  const { userCan } = useAuth();
  const {
    orgMarketId,
    scan,
    scanRoot,
    scanDataMutators,
    contractorsMap,
    baselineScanRoot,
    scanHasScopeChanges,
    projectIsEditable,
    taskIdsByAssignedContractorId,
  } = useScanEditingContextSlice([
    'scan',
    'scanRoot',
    'scanDataMutators',
    'orgMarketId',
    'contractorsMap',
    'baselineScanRoot',
    'scanHasScopeChanges',
    'projectIsEditable',
    'taskIdsByAssignedContractorId',
  ]);
  const { showToast } = useToast();
  const confirm = useConfirmationModal();
  const canEditScan = projectIsEditable && userCan('update', 'Scan');
  const canShareWithContractors = !!featureFlags.contractorOnboarding && userCan('update', 'PropertyProject');

  const checkEditableWithToast = useScanIsEditableCheck(showToast);

  const updateDefaultContractorsByTrade = (trade: string, contractorId: string | null) => {
    if (!checkEditableWithToast() || !scanRoot) return;

    scanDataMutators.updateScan({ defaultContractorsByTrade: merge(scanRoot.defaultContractorsByTrade, { [trade]: contractorId }) as Record<string, string> });
  };

  const configuredTrades = allOrgSettings?.tradeSettings?.trades as
    | Record<string, TradeSettingsTrade>
    | undefined;
  const orderedTrades = useMemo(
    () =>
      getOverviewTradeOrder({
        configuredTrades,
        scanRoot,
      }),
    [configuredTrades, scanRoot],
  );

  if (!scan || !scanRoot || !contractorsMap) return null;

  const scanScopeEditable = isScanEditable(scan);
  const isChangeOrder = scanHasScopeChanges;
  const shouldShowChanges = isChangeOrder && scanScopeEditable && baselineScanRoot;

  // Non-editing view lists the contractors actually assigned to scoped tasks (resolved per task,
  // including trade defaults via taskIdsByAssignedContractorId) — not just the per-trade default picks.
  const assignedContractorsFromTasks = Object.keys(taskIdsByAssignedContractorId).map(contractorId => ({
    contractorId,
    name: contractorsMap[contractorId]?.name ?? 'Unknown contractor',
    taskCount: taskIdsByAssignedContractorId[contractorId].length as number | undefined,
  }));

  // Backward compatibility: scans that predate scoped-task assignment (or that only have per-trade
  // defaults) resolve to no scoped tasks, so the task-based list is empty. Fall back to the old
  // behavior — the distinct contractors from defaultContractorsByTrade — so they still show up.
  const assignedContractorsFromTradeDefaults = [
    ...new Set(
      orderedTrades
        .map(trade => scanRoot.defaultContractorsByTrade?.[trade])
        .filter((contractorId): contractorId is string => present(contractorId)),
    ),
  ].map(contractorId => ({
    contractorId,
    name: contractorsMap[contractorId]?.name ?? 'Unknown contractor',
    taskCount: undefined as number | undefined,
  }));

  const assignedContractors = assignedContractorsFromTasks.length > 0
    ? assignedContractorsFromTasks
    : assignedContractorsFromTradeDefaults;

  const hasAnyChanges = shouldShowChanges && orderedTrades.some(trade =>
    contractorHasChanged(
      trade,
      scanRoot.defaultContractorsByTrade,
      baselineScanRoot?.defaultContractorsByTrade,
    )
  );

  const revertChanges = () => {
    if (!checkEditableWithToast() || !baselineScanRoot || !scanRoot) return;
    confirm({
      title: 'Revert Changes',
      message: 'Are you sure you want to revert the changes to the default contractors?',
      onConfirm: () => {
        scanDataMutators.updateScan({ defaultContractorsByTrade: baselineScanRoot.defaultContractorsByTrade ?? {} });
      },
    });
  };

  return (
    <PropsCard
      title={
        <div css={CONTAINER_STYLE}>
          <LargeContractorIcon />
          <div css={MAIN_COLUMN_STYLE}>
            <div css={MAIN_TITLE_STYLE}>
              {hasAnyChanges && <DeltaIcon size={TYPOGRAPHY2.fontSize.medium} color={COLORS2.alwaysDark.red} />}
              Contractor Assignments
            </div>
            <div css={SHORT_INSTRUCTION_STYLE}>
              Review contractor assignments before starting. Once selected, they will appear as the default contractor per trade.
            </div>
          </div>
        </div>
      }
      buttons={canEditScan
        ? [
          hasAnyChanges && { label: 'Revert Changes', onClick: revertChanges, buttonStyle: 'yellow' },
          scanScopeEditable && { label: editMode ? 'Done' : 'Edit', onClick: () => setEditMode(!editMode) },
        ]
        : []}
      columnCount={3}
      items={editMode
        ? orderedTrades.map(trade => {
          return (
            <DefaultContractorPicker
              key={trade}
              scanRoot={scanRoot}
              updateDefaultContractorsByTrade={updateDefaultContractorsByTrade}
              trade={trade}
              orgMarketId={orgMarketId}
            />
          );
        })
        : assignedContractors.length > 0
        ? assignedContractors.map(contractor => ({
          label: contractor.name,
          value: (
            <div css={css`display: flex; align-items: center; justify-content: space-between; gap: ${LAYOUT2.spacingHalf}px;`}>
              <span css={SHORT_INSTRUCTION_STYLE}>
                {contractor.taskCount != null ? `${contractor.taskCount} task${contractor.taskCount === 1 ? '' : 's'}` : ''}
              </span>
              {canShareWithContractors && <CopyShareLinkButton scanId={scan.id} orgContractorId={contractor.contractorId} />}
            </div>
          ),
        }))
        : [{ label: 'No contractors assigned' }]}
    />
  );
};
